import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';
import {
  CustomVariableSchema,
  DisplayTypeSchema,
  ModelTypeSchema,
  VarTypeSchema,
} from './use-load-custom-variables';

const URL = '/custom_variables';

export const CreateCustomVariableVariablesSchema = z.object({
  name: z.string().min(1),
  modelType: ModelTypeSchema,
  varType: VarTypeSchema,
  api: z.boolean(),
  webhook: z.boolean(),
  display: DisplayTypeSchema.nullish(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).nullish(),
});

export type CreateCustomVariableVariables = z.infer<typeof CreateCustomVariableVariablesSchema>;

const CreateCustomVariableResponseSchema = z.intersection(
  CustomVariableSchema,
  z.object({ message: z.string() }),
);

type CreateCustomVariableResponseBody = z.infer<typeof CreateCustomVariableResponseSchema>;

export async function createCustomVariableFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateCustomVariableVariables,
): Promise<AxiosResponse<CreateCustomVariableResponseBody>> {
  const parsed = CreateCustomVariableVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  const response = await instance.post(URL, parsed);
  CreateCustomVariableResponseSchema.parse(response.data);
  return response as AxiosResponse<CreateCustomVariableResponseBody>;
}

export function useCreateCustomVariable() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<
    AxiosResponse<CreateCustomVariableResponseBody>,
    ApiError,
    CreateCustomVariableVariables
  >({
    mutationFn: variables => createCustomVariableFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['customVariables'] });
      client.invalidateQueries({ queryKey: ['company-configuration'] });
    },
  });

  return addActionNameToMutationResult<
    AxiosResponse<CreateCustomVariableResponseBody>,
    ApiError,
    CreateCustomVariableVariables,
    'createCustomVariable'
  >('createCustomVariable', mutation);
}
